// ABOUTME: Defines shared integration platform TypeScript types and activity input contracts.
import { RaasCredentialConfig } from '../activities/api/raas/raas.zod';
import { ActivityType, EntityType, Source } from './enums';
import { TechlogTruckUnit } from './techLog/fuel';

/**
 * Firestore Timestamp serialized format.
 * When Firestore Timestamps are passed through Temporal or JSON serialization,
 * they become objects with _seconds and _nanoseconds properties.
 */
export type FirestoreTimestamp = {
  _seconds: number;
  _nanoseconds: number;
};

export interface PollingAdapter {
  handleEntity(entityType: EntityType, pollingItem: any): Promise<void>;
}

export interface APIErrorResponse {
  error: {
    url: string;
    status: number;
    // ...
  };
}

// Auth types/interfaces

// This is the type of the "auth" object that is passed to the activity
// function. It is used to authorize the user to perform the activity and
// to provide the activity with any other information it may need to
// complete the activity.
// Any interaction with an integration should be able to be performed using this auth data.
export interface LeonAuth {
  type: ActivityType.LEON;
  operatorId: string;
  createdAt?: FirestoreTimestamp;
}
export interface NavblueAuth {
  type: ActivityType.NAVBLUE;
  operatorId: string;
}

export interface TechlogAuth {
  type: ActivityType.TECHLOG;
  operatorId: string;
  userId?: string;
  apiKeyCredentialDocId?: string;
}

export interface AmosAuth {
  type: ActivityType.AMOS;
  url: string;
  token?: string;
}

export interface TraxxallAuth {
  type: ActivityType.TRAXXALL;
  operatorId: string;
}

export interface RusadaAuth {
  type: ActivityType.RUSADA;
  operatorId: string;
}
export interface Fl3xxAuth {
  type: ActivityType.FL3XX;
  operatorId: string;
  useStaging?: boolean;
  baseAPIUrl?: string;
}
export interface BlueOneAuth {
  type: ActivityType.BLUEONE;
  operatorId: string;
}

export interface AstroAuth {
  type: ActivityType.ASTRO;
  operatorId: string;
}

export interface CampAuth {
  type: ActivityType.CAMP;
  operatorId: string;
}

export interface RaasAuth {
  type: ActivityType.RAAS;
  operatorId: string;
  url: string;
}

export interface SkylegsAuth {
  type: ActivityType.SKYLEGS;
  operatorId: string;
}

export interface IntegrationPlatformAuth {
  type: ActivityType.INTEGRATION_PLATFORM;
  operatorId?: string;
}

export type ActivityAuth =
  | LeonAuth
  | TechlogAuth
  | AmosAuth
  | TraxxallAuth
  | RusadaAuth
  | NavblueAuth
  | Fl3xxAuth
  | BlueOneAuth
  | AstroAuth
  | CampAuth
  | RaasAuth
  | SkylegsAuth
  | IntegrationPlatformAuth;

export type ActivityInputAuth = [ActivityAuth, ...ActivityAuth[]];

export interface ActivityInput {
  auth: ActivityInputAuth;
}

// AMOS
export interface IAmosRequestData {
  xml?: string;
  json?: any;
}

export type AMOSWorkpackage = any;

export type IntegrationUserType = {
  email: string;
  firstName: string;
  lastName: string;
  position?: string;
  amosUserSign: string;
  employeeNumber?: string;
};

export type IntegrationUserCapabilityDateItem = {
  startDate?: string;
  issueDate?: string;
  expiryDate?: string;
};

export type AMOSUser = {
  userSign: string;
  signHeader: {
    email: string;
    firstName: string;
    lastName: string;
    status: number;
    expireDate: string;
    validUntilDate: string;
    // unsure if this is the correct location:
    employeeNumber: string;
    qualification: {
      qualificationId: Array<{
        categoryId: string;
        typeId: string;
        qualificationData: any;
      }>;
    };
  };
};

export type AMOSQualificationDataItem = {
  dataId: string;
  value: string;
};

export type AMOSDocumentApprovalInput = {
  approvalNumber: string;
  releaseIssuer: string;
  newReleaseState: string;
  remarks?: string;
};

export type TechlogUser = {
  id: string;
  operatorId: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string; //enum?
  employeeNumber: string;
  amosUserSign: string;
  status: string; //enum?
  hasLogin: boolean;
  createdAt: string;
  updatedAt: string;
};

export type TechlogUserCreateInput = {
  email: string;
  firstName: string;
  lastName: string;
  position?: string;
  userSign?: string;
  employeeNumber?: string;
};

export type RectificationIntervalExtension = {
  id: string;
  defectId: string;
  extensionDate: string;
  extensionDateDue: string;
  createdAt: string;
  updatedAt: string;
};

export type TechlogDefectResponse = {
  id: string;
  aircraftId: string;
  sectorId: string;
  reportAirportId: number;
  resolutionAirportId: number | null;
  details: string;
  itemDescription: string;
  number: number;
  rectificationIntervalExtension: RectificationIntervalExtension;
  dueDate: string;
  dueDateTime: Date | undefined;
  estimatedDate: string;
  raisedAt: string;
  dueFlightSeconds: number;
  dueApuSeconds: number;
  dueCycles: number;
  dueLandings: number;
  status: string;
  reference: string;
  ataChapter?: number;
  ataSection?: number;
  flightsDue: number;
  limitations: string;
  melItemId: string;
  melRectificationId: string;
  placardProcedureCompleted: boolean;
  operationalProcedureCompleted: boolean;
  deferred: boolean;
  raisedFromId: string;
  reportedById: string;
  source: Source;
  importedAt: string;
  defectType: string;
  rectificationCategory: string;
  tripEntryId: string;
  serviceabilityLimiting: boolean;
  limitFlightSeconds: number;
  limitSeconds: number;
  limitFlightDays: number;
  limitCycles: number;
  limitFlights: number;
  limitCalendarDays: number;
  limitApuSeconds: number;
  limitOther: string;
  deferApprovalReference: string;
  reasonToDefer: string;
  editedById: string;
  editedDate: string;
  eropsPrevented: boolean;
  placardNumber: number;
  campSyncDate: string;
  campReference: string;
  deferralSignatureId: string;
  deferredAt: string;
  deferralLicenceNumber: string;
  deferralApprovalNumber: string;
  deferredById: string;
  intermittentFaultId: string;
  reportedByType: string;
  createdAt: string;
  updatedAt: string;
  deferUsing: string;
  thirdPartyReferences: {
    [key: string]: string;
  };
  troubleshootingSteps: string;
};

export type TechlogDefectResponsev2 = {
  id: string;
  aircraftId: string;
  sectorId: string;
  reportAirportId: number;
  dueDate: string;
  dueDateTime: string;
  details: string;
  itemDescription: string | null;
  number: number;
  status: string;
  raisedAt: string;
  reference: string | null;
  ataChapter: number;
  ataSection: number;
  limitations: string | null;
  bookItemId: string;
  bookItemType: string;
  bookItem: {
    id: string;
    chapterNumber: number;
    subsectionNumber: number;
    subsubsection: string | null;
  };
  rectificationId: string;
  placardProcedureCompleted: boolean | null;
  operationalProcedureCompleted: boolean | null;
  deferred: boolean;
  reportedById: string;
  defectType: string;
  rectificationCategory: string;
  tripEntryId: string | null;
  serviceabilityLimiting: boolean;
  limitFlightSeconds: number | null;
  limitSeconds: number | null;
  limitFlightDays: number | null;
  limitCycles: number | null;
  limitFlights: number | null;
  limitCalendarDays: number | null;
  limitApuSeconds: number | null;
  limitOther: string | null;
  deferApprovalReference: string;
  reasonToDefer: string;
  eropsPrevented: boolean | null;
  placardNumber: number | null;
  deferredAt: string;
  deferralLicenceNumber: string;
  deferralApprovalNumber: string | null;
  deferredById: string;
  reportedByType: string;
  deferUsing: string;
  intermittentFaultId: string | null;
  deferralAppliedAt: string;
  thirdPartyReferences: {
    [key: string]: string;
  };
  troubleshootingSteps: string;
  createdAt: string;
  updatedAt: string;
};

export type FlightFuelSummaryResponse = {
  preUpliftFuel: number;
  postUpliftFuel: number;
  usedFuel: number;
  remainingFuel: number;
};

export interface SyncedData {
  origin: string;
  originId: string;
  destination: string; //ENUM
  destinationId: string;
  type: string; //ENUM

  originMetadata?: any;
  // {
  //   operatorId?: string;
  // };
  destinationMetadata?: any;
}

export type TechlogOperatorPersonRoles = {
  id: string;
  name: string;
  crewType: string;
  positions: Array<string>;
  dutyTracked: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type TechlogOperatorResponse = {
  id: string;
  name: string;
  address: string;
  phoneNumber: string;
  aocNumber: string;
  createdAt: Date;
  updatedAt: Date;
  personRoles: Array<TechlogOperatorPersonRoles>;
};

export type TechlogAttachment = {
  id: string;
  contentType: string;
  size: number;
  name: string;
};

export type TechlogReleaseResponse = {
  id: string;
  aircraftId: string;
  operatorId: string;
  signatureId: string;
  approvalSignatureId: string;
  workpackId: string;
  openDefectIds: Array<string>;
  additionalItems: Array<string>;
  mxItemIds: Array<string>;
  date: string;
  companyName: string;
  approvalNumber: string;
  firstName: string;
  lastName: string;
  licenceNumber: string;
  companyEmail: string;
  customData: unknown;
  createdAt: Date;
  updatedAt: Date;
  reference: string;
  reporterFirstName: string;
  reporterLastName: string;
  approverFirstName: string;
  approverLastName: string;
  signeeType: string;
  attachments: Array<TechlogAttachment>;
};

export type TechlogAircraftResponse = {
  id: string;
  operatorId: string;
  aircraftTypeId: string;
  serialNumber: string;
  registration: string;
  year: number;
  fuelUnit: string;
  oilUnit: string;
  maxFuelValue: number;
  monthlyCycles: number;
  monthlyFlightSeconds: number;
  monthlyApuSeconds: number;
  maintenanceType: string;
  apuMode: string;
  flightStatus: string;
  locked: boolean;
  billingStatus: string;
  createdAt: Date;
  updatedAt: Date;
};

export type attachments = {
  id: string;
  contentType: string;
  size: number;
  name: string;
};

export type deiceEvent = {
  id: string;
  sectorId: string;
  startTime: date;
  endTime: date;
  fluidRatioLeft: number;
  fluidRatioRight: number;
  quantity: number;
  fluidType: string;
  holdoverSeconds: number;
  holdoverTime: number;
  attachments: attachments[];
  createdAt: date;
  updatedAt: date;
};

export type TechlogDeiceEvents = {
  records: deiceEvent[];
};

export type TechlogFuelUplifts = {
  id: string;
  associatedRecordId: string;
  associatedRecordType: string;
  aircraftId: string;
  aircraftValuePreUplift: number;
  aircraftValuePostUplift: number;
  aircraftValueActualUplift: number;
  truckActualUplift: number;
  truckUnit: TechlogTruckUnit;
  density: number;
  fuelType: string;
  safPercentage: number;
  aircraftPlannedDeparture: number;
  priceValue: number;
  priceCurrency: string;
  paid: boolean;
  aircraftUnit: string;
  createdAt: Date;
  updatedAt: Date;
};

// TODO
// - Convert to a combination of integration specific types
/**
 * Credential records use `integration_opr_id` as the canonical external
 * operator or tenant identifier.
 *
 * Keep `operator_id` only for credential records and lookup paths that still
 * depend on it.
 *
 * FIXME: ASTRO, BLUEONE, CAMP, TRAXXALL, and NAVBLUE still resolve
 * credentials through `operator_id`.
 * FIXME: AMOS resolves credentials by `url`, and TRAXXALL does not persist
 * `integration_opr_id`.
 */
export type IntegrationCredential = {
  integration: string;
  /**
   * [Deprecated] Compatibility field for credential records that still resolve by
   * `operator_id`. Use `integration_opr_id` instead.
   */
  operator_id?: string;
  /**
   * External-system operator or tenant identifier for the credential
   * record. Use this field for credential storage and lookup.
   */
  integration_opr_id: string;
  api_user_id: string;
  refresh_token: string;
  last_updated: any;
  access_token: string;
  api_key: string;
  api_password: string;
  password: string;
  username: string;
  flightUsername: string;
  flightPassword: string;
  journeyUsername: string;
  journeyPassword: string;
  crewDetailsUsername: string;
  crewDetailsPassword: string;
  url: string;
  token: string;
  refreshToken: string;
  apiToken: string;
  newToken: string;
} & RaasCredentialConfig;

export type ENV_VARS = {
  [key: string]: string;
};

export interface SubscribeInput {
  operatorName: string;
  operatorId?: string;
  url?: string;
  operatorEmail: string;
  recipientRole?: 'client' | 'internal';
  notificationSettings: {
    [key: string]: {
      emailDelayMinutes?: number;
      enabled?: boolean;
    };
  };
}

export type dateTime = string;
export type date = string;

// a TailNumber is a string representation of Aircraft's registration number
export type TailNumber = string;

export enum StandardizedUserRole {
  CMD = 'Command', // Pilot In Command,
  FO = 'FirstOfficer', // Second In Command
  FA = 'FlightAttendant',

  OTHER = 'Other', // Fallback
}

export type StandardizedUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role: StandardizedUserRole;
};

export type StandardizedFlight = {
  originTripId?: string;
  originId?: string;
  date: string;
  tripCategory?: string; // TODO: ENUM??
  tripStatus?: string; // TODO: ENUM??
  tripNumber?: string;
  pilotInCommand?: StandardizedUser | null;
  secondInCommand?: StandardizedUser | null;
  additionalCrew: Array<StandardizedUser>;
  departureAirportICAO?: string;
  arrivalAirportICAO?: string;
  actualAirportICAO?: string;
  aircraft: string;
  number?: string;
  // ICAO flight-plan Item 8 type-of-flight designator (Leon Flight.icaoType): S/N/G/M/X.
  flightIcaoType?: string;
  tripType?: string; // TODO: ENUM??
  takeoffTime?: string;
  landingTime?: string;
  offblocksTime?: string;
  onblocksTime?: string;
  landings?: number;
  maxFlightLevel?: number;
  totalApuHours?: string;
  totalAPUCycles?: number;
  flightLog?: string;
  flightNumber?: string;
  passengers?: {
    children?: number;
    infants?: number;
    male?: number;
    female?: number;
  };
  pax?: number;

  fuel?: {
    safPercentage?: number;
    safUsed?: boolean;
    blockFuel?: number;
    usedFuel?: number;
    remainingFuel?: number;
    preUpliftFuel?: number;
    uplift?: number;
  };

  hobbsDeparture?: number;
  hobbsArrival?: number;
  deicing?: boolean; // TODO: convert to object w/ details

  postflightCustomData?: any;
  preflightCustomData?: any;
};

export { TechlogTruckUnit } from './techLog/fuel';
